<script setup>
import { computed } from 'vue';

import { HrIcon } from '../primitives';

/**
 * How serious something is, as an icon and a label coloured by the severity itself.
 *
 * The inline counterpart to the banner family: a banner owns a whole surface and announces one
 * condition, this marks one row or field among many. A table of validation issues is the case it
 * exists for.
 *
 * The caller states the severity, never a colour. That is the same contract HrErrorBanner and its
 * siblings have — they set their own `colorRole` rather than accepting one — and it is what stops a
 * palette decision being made at a call site that cannot see the rest of the table.
 *
 * @status recommended
 * @group status-feedback
 * @useWhen Marking a row, cell or field with how serious a condition is
 * @avoidWhen The condition deserves its own surface — use HrErrorBanner and its siblings
 * @avoidWhen The value is a count or a duration rather than a severity — use HrBadge
 * @avoidWhen The text names what something IS rather than how serious it is — use HrTag
 * @related HrErrorBanner
 * @related HrWarningBanner
 * @related HrTag
 * @a11y The label carries the meaning as text. Colour and icon reinforce it and never carry it
 *   alone, so the component stays readable to a colour-blind reader and in a monochrome print.
 */
defineOptions({ name: 'HrSeverityLabel' });

/** The icon each severity wears unless the caller overrides it. */
const SEVERITY_ICONS = {
  error: 'error',
  warning: 'warning',
  info: 'info',
  success: 'check_circle',
};

const props = defineProps({
  /**
   * How serious the condition is. Drives both the icon and the colour role — a caller with a
   * domain-specific set of severities maps them onto these four rather than passing a colour.
   * @values error, warning, info, success
   * @example warning
   */
  severity: {
    type: String,
    required: true,
    // Listed rather than derived from SEVERITY_ICONS: defineProps is compiled before local
    // declarations exist, so it cannot reference one.
    validator: (value) => ['error', 'warning', 'info', 'success'].includes(value),
  },
  /**
   * The text shown beside the icon. Required: the label is what carries the meaning, so the
   * component stays readable without colour.
   * @example Warning
   */
  label: {
    type: String,
    required: true,
  },
  /** Overrides the icon for this severity, where a more specific glyph says more. */
  icon: {
    type: String,
    default: null,
  },
  /**
   * Icon size token.
   * @values xxs, xs, sm, md, lg, xl
   */
  size: {
    type: String,
    default: 'sm',
    validator: (value) => ['xxs', 'xs', 'sm', 'md', 'lg', 'xl'].includes(value),
  },
});

const iconName = computed(() => props.icon ?? SEVERITY_ICONS[props.severity]);
</script>

<template>
  <span :class="['hr-severity-label', `hr-severity-label--${props.severity}`]">
    <hr-icon
      :name="iconName"
      :size="props.size"
      class="hr-severity-label__icon"
    />
    <span class="hr-severity-label__text">{{ props.label }}</span>
  </span>
</template>

<style lang="scss" scoped>
// Each role falls back to the Quasar semantic colour it replaces, so the component still reads
// correctly under the legacy theme where the --app-* tokens are undefined.
.hr-severity-label {
  display: inline-flex;
  align-items: center;
  gap: var(--app-comp-severity-label-gap);
}

.hr-severity-label__icon { color: var(--_severity-color); }

.hr-severity-label--error   { --_severity-color: var(--app-error,   var(--q-negative)); }
.hr-severity-label--warning { --_severity-color: var(--app-warning, var(--q-warning)); }
.hr-severity-label--info    { --_severity-color: var(--app-primary, var(--q-info)); }
.hr-severity-label--success { --_severity-color: var(--app-success, var(--q-positive)); }
</style>
